import React, { useState, useEffect } from 'react';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { FilterMatchMode, FilterOperator } from 'primereact/api';
import { Dropdown } from 'primereact/dropdown';
import { formatDateOnly } from '../../utils/dateUtils';

// Mock data for demonstration
const mockParts = [
    {
        id: 1,
        partCode: "PT-12345",
        partName: "Şanzıman Dişlisi",
        category: "Transmission",
        warehouse: "Ana Depo",
        currentStock: 250,
        reservedStock: 75,
        availableStock: 175,
        minimumStock: 50,
        maximumStock: 300,
        unitPrice: 1200,
        totalValue: 300000,
        lastStockUpdate: "2025-03-15",
        status: "Normal"
    },
    {
        id: 2,
        partCode: "PT-45678",
        partName: "Fren Balataları",
        category: "Brake",
        warehouse: "Ana Depo",
        currentStock: 120,
        reservedStock: 80,
        availableStock: 40,
        minimumStock: 50,
        maximumStock: 200,
        unitPrice: 350,
        totalValue: 42000,
        lastStockUpdate: "2025-03-17",
        status: "Low"
    },
    {
        id: 3,
        partCode: "PT-98765",
        partName: "Motor Valfleri",
        category: "Engine",
        warehouse: "Yedek Depo",
        currentStock: 500,
        reservedStock: 100,
        availableStock: 400,
        minimumStock: 100,
        maximumStock: 600,
        unitPrice: 250,
        totalValue: 125000,
        lastStockUpdate: "2025-03-18",
        status: "Normal"
    },
    {
        id: 4,
        partCode: "PT-24680",
        partName: "Yakıt Pompası",
        category: "Fuel",
        warehouse: "Ana Depo",
        currentStock: 30,
        reservedStock: 25,
        availableStock: 5,
        minimumStock: 20,
        maximumStock: 100,
        unitPrice: 1800,
        totalValue: 54000,
        lastStockUpdate: "2025-03-19",
        status: "Critical"
    },
    {
        id: 5,
        partCode: "PT-13579",
        partName: "Alternatör",
        category: "Electrical",
        warehouse: "Yedek Depo",
        currentStock: 350,
        reservedStock: 50,
        availableStock: 300,
        minimumStock: 40,
        maximumStock: 400,
        unitPrice: 950,
        totalValue: 332500,
        lastStockUpdate: "2025-03-20",
        status: "Overstocked"
    }
];

const categoryOptions = [
    { name: 'Şanzıman', code: 'Transmission' },
    { name: 'Fren', code: 'Brake' },
    { name: 'Motor', code: 'Engine' },
    { name: 'Yakıt', code: 'Fuel' },
    { name: 'Elektrik', code: 'Electrical' }
];

const warehouseOptions = [
    { name: 'Ana Depo', code: 'Ana Depo' },
    { name: 'Yedek Depo', code: 'Yedek Depo' }
];

const statusOptions = [
    { name: 'Normal', code: 'Normal' },
    { name: 'Düşük', code: 'Low' },
    { name: 'Kritik', code: 'Critical' },
    { name: 'Fazla Stok', code: 'Overstocked' }
];

const PartsBalanceListPage = () => {
    const [parts, setParts] = useState([]);
    const [loading, setLoading] = useState(true);
    const [globalFilterValue, setGlobalFilterValue] = useState('');
    const [filters, setFilters] = useState({
        global: { value: null, matchMode: FilterMatchMode.CONTAINS },
        partCode: { 
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.STARTS_WITH }] 
        },
        partName: { 
            operator: FilterOperator.AND,
            constraints: [{ value: null, matchMode: FilterMatchMode.CONTAINS }] 
        },
        category: { value: null, matchMode: FilterMatchMode.EQUALS },
        warehouse: { value: null, matchMode: FilterMatchMode.EQUALS },
        status: { value: null, matchMode: FilterMatchMode.EQUALS }
    });
    const [stockSummary, setStockSummary] = useState({
        totalParts: 0,
        totalValue: 0,
        lowStockCount: 0,
        criticalStockCount: 0,
        overstockedCount: 0
    });

    useEffect(() => {
        setParts(mockParts);
        setLoading(false);

        // Calculate summary
        const summary = {
            totalParts: mockParts.length,
            totalValue: mockParts.reduce((sum, part) => sum + part.totalValue, 0),
            lowStockCount: mockParts.filter(part => part.status === 'Low').length,
            criticalStockCount: mockParts.filter(part => part.status === 'Critical').length,
            overstockedCount: mockParts.filter(part => part.status === 'Overstocked').length
        };
        setStockSummary(summary);
    }, []);

    const onGlobalFilterChange = (e) => {
        const value = e.target.value;
        let _filters = { ...filters };
        
        _filters['global'].value = value;
        
        setFilters(_filters);
        setGlobalFilterValue(value);
    };

    const header = (
        <div className="flex justify-content-between align-items-center">
            <div className="flex align-items-center">
                <span className="p-input-icon-left">
                    <i className="pi pi-search" />
                    <InputText 
                        value={globalFilterValue} 
                        onChange={onGlobalFilterChange} 
                        placeholder="Ara..." 
                    />
                </span>
            </div>
            <div className="flex gap-2">
                <Button 
                    label="Excel Export" 
                    icon="pi pi-file-excel" 
                    severity="success" 
                />
                <Button 
                    label="PDF Export" 
                    icon="pi pi-file-pdf" 
                    severity="warning" 
                />
            </div>
        </div>
    );

    const formatCurrency = (value) => {
        return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(value);
    };

    const statusBodyTemplate = (rowData) => {
        let severity = '';
        let statusText = '';
        
        switch (rowData.status) {
            case 'Normal':
                severity = 'success';
                statusText = 'Normal';
                break;
            case 'Low':
                severity = 'warning';
                statusText = 'Düşük';
                break;
            case 'Critical':
                severity = 'danger';
                statusText = 'Kritik';
                break;
            case 'Overstocked':
                severity = 'info';
                statusText = 'Fazla Stok';
                break;
            default:
                severity = 'info';
                statusText = rowData.status;
        }
        
        return (
            <span className={`p-badge p-badge-${severity}`}>
                {statusText}
            </span>
        );
    };

    const getCategoryName = (code) => {
        const option = categoryOptions.find(o => o.code === code);
        return option ? option.name : code;
    };

    return (
        <div className="card">
            <div className="flex justify-content-between align-items-center mb-4">
                <h1 className="m-0">Parça Bakiye Listesi</h1>
            </div>
            
            <div className="grid mb-4">
                <div className="col-12 md:col-6 lg:col-3">
                    <Card className="bg-blue-50">
                        <div className="flex justify-content-between">
                            <div>
                                <span className="block text-500 font-medium mb-3">Toplam Parça</span>
                                <div className="text-900 font-medium text-xl">{stockSummary.totalParts}</div>
                            </div>
                            <div className="flex align-items-center justify-content-center bg-blue-100 border-round" style={{ width: '2.5rem', height: '2.5rem' }}>
                                <i className="pi pi-shopping-cart text-blue-500 text-xl" />
                            </div>
                        </div>
                    </Card>
                </div>
                
                <div className="col-12 md:col-6 lg:col-3">
                    <Card className="bg-green-50">
                        <div className="flex justify-content-between">
                            <div>
                                <span className="block text-500 font-medium mb-3">Toplam Değer</span>
                                <div className="text-900 font-medium text-xl">{formatCurrency(stockSummary.totalValue)}</div>
                            </div>
                            <div className="flex align-items-center justify-content-center bg-green-100 border-round" style={{ width: '2.5rem', height: '2.5rem' }}>
                                <i className="pi pi-money-bill text-green-500 text-xl" />
                            </div>
                        </div>
                    </Card>
                </div>
                
                <div className="col-12 md:col-6 lg:col-3">
                    <Card className="bg-yellow-50">
                        <div className="flex justify-content-between">
                            <div>
                                <span className="block text-500 font-medium mb-3">Düşük/Kritik Stok</span>
                                <div className="text-900 font-medium text-xl">{stockSummary.lowStockCount + stockSummary.criticalStockCount}</div>
                            </div>
                            <div className="flex align-items-center justify-content-center bg-yellow-100 border-round" style={{ width: '2.5rem', height: '2.5rem' }}>
                                <i className="pi pi-exclamation-triangle text-yellow-500 text-xl" />
                            </div>
                        </div>
                    </Card>
                </div>
                
                <div className="col-12 md:col-6 lg:col-3">
                    <Card className="bg-purple-50">
                        <div className="flex justify-content-between">
                            <div>
                                <span className="block text-500 font-medium mb-3">Fazla Stok</span>
                                <div className="text-900 font-medium text-xl">{stockSummary.overstockedCount}</div>
                            </div>
                            <div className="flex align-items-center justify-content-center bg-purple-100 border-round" style={{ width: '2.5rem', height: '2.5rem' }}>
                                <i className="pi pi-chart-bar text-purple-500 text-xl" />
                            </div>
                        </div>
                    </Card>
                </div>
            </div>
            
            <Card>
                <DataTable 
                    value={parts} 
                    paginator 
                    className="p-datatable-gridlines"
                    showGridlines
                    rows={10} 
                    dataKey="id"
                    loading={loading}
                    filters={filters}
                    filterDisplay="menu"
                    globalFilterFields={['partCode', 'partName', 'category', 'warehouse']}
                    header={header}
                    emptyMessage="Parça kaydı bulunamadı."
                    rowsPerPageOptions={[5, 10, 25, 50, 100, 500, 1000]} 
                    tableStyle={{ minWidth: '50rem' }}
                >
                    <Column 
                        field="partCode" 
                        header="Parça Kodu" 
                        filter
                        filterPlaceholder="Parça kodu ara..."
                        sortable 
                        style={{ width: '120px' }}
                    />
                    <Column 
                        field="partName" 
                        header="Parça Adı" 
                        filter
                        filterPlaceholder="Parça adı ara..."
                        sortable 
                        style={{ width: '180px' }}
                    />
                    <Column 
                        field="category" 
                        header="Kategori" 
                        body={(rowData) => getCategoryName(rowData.category)}
                        filter
                        filterElement={(options) => (
                            <Dropdown
                                value={options.value}
                                options={categoryOptions}
                                onChange={(e) => options.filterCallback(e.value)}
                                optionLabel="name"
                                optionValue="code"
                                placeholder="Tümü"
                                className="p-column-filter"
                            />
                        )}
                        sortable 
                        style={{ width: '120px' }}
                    />
                    <Column 
                        field="warehouse" 
                        header="Depo" 
                        filter
                        filterElement={(options) => (
                            <Dropdown
                                value={options.value}
                                options={warehouseOptions}
                                onChange={(e) => options.filterCallback(e.value)}
                                optionLabel="name"
                                optionValue="code"
                                placeholder="Tümü"
                                className="p-column-filter"
                            />
                        )}
                        sortable 
                        style={{ width: '120px' }}
                    />
                    <Column 
                        field="currentStock" 
                        header="Mevcut Stok" 
                        sortable 
                        style={{ width: '120px' }}
                    />
                    <Column 
                        field="reservedStock" 
                        header="Rezerve Stok" 
                        sortable 
                        style={{ width: '120px' }}
                    />
                    <Column 
                        field="availableStock" 
                        header="Kullanılabilir Stok" 
                        sortable 
                        style={{ width: '150px' }}
                    />
                    <Column 
                        field="minimumStock" 
                        header="Min. Stok" 
                        sortable 
                        style={{ width: '100px' }}
                    />
                    <Column 
                        field="maximumStock" 
                        header="Max. Stok" 
                        sortable 
                        style={{ width: '100px' }}
                    />
                    <Column 
                        field="unitPrice" 
                        header="Birim Fiyat" 
                        body={(rowData) => formatCurrency(rowData.unitPrice)}
                        sortable 
                        style={{ width: '120px' }}
                    />
                    <Column 
                        field="totalValue" 
                        header="Toplam Değer" 
                        body={(rowData) => formatCurrency(rowData.totalValue)}
                        sortable 
                        style={{ width: '150px' }}
                    />
                    <Column 
                        field="lastStockUpdate" 
                        header="Son Stok Güncelleme"
                        sortable
                        style={{ width: '200px' }}
                        body={(rowData) => formatDateOnly(rowData.lastStockUpdate)}
                    />
                    <Column 
                        field="status" 
                        header="Durum" 
                        body={statusBodyTemplate}
                        filter
                        filterElement={(options) => (
                            <Dropdown
                                value={options.value}
                                options={statusOptions}
                                onChange={(e) => options.filterCallback(e.value)}
                                optionLabel="name"
                                optionValue="code"
                                placeholder="Tümü"
                                className="p-column-filter"
                            />
                        )}
                        sortable 
                        style={{ width: '120px' }}
                    />
                </DataTable>
            </Card>
        </div>
    );
};

export default PartsBalanceListPage;
